import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import Modal from "../../components/Modal";
import { useToast } from "../../lib/toast";

interface Supplier {
  id: string; code: string; name: string;
  business_no?: string | null; representative?: string | null;
  phone?: string | null; email?: string | null; address?: string | null;
  memo?: string | null; active: number; created_at: number;
}

export default function AdminSuppliers() {
  const [items, setItems] = useState<Supplier[]>([]);
  const [editing, setEditing] = useState<Supplier | "new" | null>(null);
  const [loading, setLoading] = useState(true);
  const { push } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ items: Supplier[] }>("/admin/suppliers")
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form: Partial<Supplier>) => {
    try {
      if (editing === "new") await api.post("/admin/suppliers", form);
      else if (editing) await api.post(`/admin/suppliers/${editing.id}`, form);
      push(editing === "new" ? "매입처가 추가되었습니다" : "매입처가 수정되었습니다", "success");
      setEditing(null);
      load();
    } catch (e: any) {
      push(e.message, "error");
    }
  };

  const remove = async (s: Supplier) => {
    if (!window.confirm(`${s.code} ${s.name}을(를) 비활성화할까요?`)) return;
    try {
      await api.post(`/admin/suppliers/${s.id}/delete`);
      push(`${s.name} 비활성화됨`, "info");
      load();
    } catch (e: any) {
      push(e.message, "error");
    }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 className="h-section" style={{ margin: 0 }}>매입처 {items.length}곳</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing("new")}>+ 추가</button>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <table>
          <thead><tr><th>코드</th><th>업체명</th><th>대표</th><th>연락처</th><th>이메일</th><th>활성</th><th></th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: "center" }} className="muted">로딩 중...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: "center" }} className="muted">등록된 매입처가 없습니다.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id}>
                <td className="mono" style={{ fontSize: 15 }}>{s.code}</td>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td style={{ fontSize: 15 }}>{s.representative ?? "—"}</td>
                <td style={{ fontSize: 15 }}>{s.phone ?? "—"}</td>
                <td style={{ fontSize: 15 }}>{s.email ?? "—"}</td>
                <td><span className={`badge ${s.active ? "badge-success" : "badge-muted"}`}>{s.active ? "활성" : "비활성"}</span></td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(s)}>수정</button>
                  {s.active ? <button className="btn btn-ghost btn-sm" onClick={() => remove(s)} style={{ color: "var(--danger)" }}>비활성화</button> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <Editor initial={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
}

function Editor({ initial, onClose, onSave }: { initial?: Supplier; onClose: () => void; onSave: (f: Partial<Supplier>) => void }) {
  const [form, setForm] = useState<Partial<Supplier>>(initial ?? { code: "", name: "", active: 1 });
  const up = (k: keyof Supplier) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <Modal
      title={initial ? "매입처 수정" : "매입처 추가"}
      subtitle={initial ? `${initial.code} · ${initial.name}` : "매입 거래처 정보 등록"}
      onClose={onClose}
    >
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="stack">
        <F label="코드 * (영대문자/숫자/하이픈)" value={form.code ?? ""} onChange={up("code")} required disabled={!!initial} placeholder="S-001" />
        <F label="업체명 *" value={form.name ?? ""} onChange={up("name")} required />
        <F label="사업자등록번호" value={form.business_no ?? ""} onChange={up("business_no")} placeholder="000-00-00000" />
        <F label="대표자" value={form.representative ?? ""} onChange={up("representative")} />
        <F label="연락처" value={form.phone ?? ""} onChange={up("phone")} />
        <F label="이메일" value={form.email ?? ""} onChange={up("email")} type="email" />
        <F label="주소" value={form.address ?? ""} onChange={up("address")} />
        <F label="메모" value={form.memo ?? ""} onChange={up("memo")} />
        <label className="row"><input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked ? 1 : 0 })} style={{ width: "auto" }} /> <span>활성</span></label>
        <div className="row">
          <button type="button" className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
          <button className="btn btn-primary" style={{ flex: 1 }}>{initial ? "저장" : "추가"}</button>
        </div>
      </form>
    </Modal>
  );
}

function F({ label, ...rest }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <input {...rest} />
    </label>
  );
}
